import { Link } from 'react-router-dom';
import { useSite } from '../context/SiteContext';
import { useCategories } from '../hooks/useCategories';
import { getCategoryImage } from '../config/categoryImages';
import {
  pageMeta,
  buildLocalBusinessJsonLd,
  buildBreadcrumbJsonLd,
} from '../config/seo';
import DigitalInvitePreview from '../components/DigitalInvitePreview';
import OptimizedImage from '../components/OptimizedImage';
import SeoHead from '../components/SeoHead';
import './Home.css';

const publicUrl = process.env.PUBLIC_URL ?? '';

const heroImage = `${publicUrl}/images/products/davetiyeler/dugun/klasik-zarif.jpg`;

const trustItems = [
  { title: 'Özel tasarım', desc: 'Her davetiyeyi size özel hazırlıyoruz.' },
  { title: 'Kaliteli baskı', desc: 'Seçkin kağıt ve baskı teknikleri.' },
  { title: 'Eskişehir', desc: 'Yerel üretim, hızlı iletişim.' },
  { title: 'Dijital seçenek', desc: 'QR kodlu online davetiye.' },
];

export default function Home() {
  const { siteName, getWhatsAppUrl, featuredDigitalSlug } = useSite();
  const { categories: davetiyeCategories } = useCategories('davetiyeler');
  const { categories: sekerlikCategories } = useCategories('sekerlik');
  const whatsappHref = getWhatsAppUrl();
  const meta = pageMeta.home;

  return (
    <main className="page home-page">
      <SeoHead
        title={meta.title}
        description={meta.description}
        path={meta.path}
        image={heroImage}
        jsonLd={[
          buildLocalBusinessJsonLd(),
          buildBreadcrumbJsonLd([{ name: 'Anasayfa', path: '/' }]),
        ]}
      />
      <section className="hero">
        <div className="hero__media">
          <OptimizedImage
            src={heroImage}
            alt="Eskişehir davetiye modelleri"
            className="hero__img"
            width={1600}
            height={900}
            decoding="async"
            fetchPriority="high"
          />
          <div className="hero__overlay" aria-hidden />
        </div>
        <div className="container hero__content">
          <p className="hero__eyebrow">{siteName}</p>
          <h1 className="hero__title">
            Eskişehir davetiye
            <br />
            modelleri ve çeşitleri
          </h1>
          <p className="hero__lead">
            Ekonomik davetiye, sade davetiye ve lüks davetiye seçenekleriyle
            düğün, nişan ve sünnet davetlerinizi tamamlayın — şekerlik ve dijital
            davetiye dahil.
          </p>
          <div className="hero__actions">
            <Link to="/davetiyeler" className="btn btn--gold">
              Koleksiyonu incele
            </Link>
          </div>
        </div>
      </section>

      <section className="trust" aria-label="Neden biz">
        <div className="container">
          <ul className="trust__grid">
            {trustItems.map((item) => (
              <li key={item.title} className="trust__item">
                <strong className="trust__title">{item.title}</strong>
                <span className="trust__desc">{item.desc}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="home-seo section" aria-labelledby="home-seo-heading">
        <div className="container home-seo__inner">
          <h2 id="home-seo-heading" className="home-seo__title">
            Davetiye modelleri her bütçeye ve tarza uygun
          </h2>
          <p className="home-seo__text">
            {siteName} olarak Eskişehir’de davetiye çeşitleri sunuyoruz: bütçe
            dostu ekonomik davetiye, minimal çizgili sade davetiye ve özel baskılı
            lüks davetiye modelleri. Düğün, nişan ve sünnet koleksiyonlarımızın
            yanı sıra davetiyenizle uyumlu şekerlik setleri ve QR kodlu dijital
            davetiye seçenekleriyle tek noktadan ilerleyebilirsiniz.
          </p>
        </div>
      </section>

      <section className="digital-spotlight" aria-labelledby="digital-heading">
        <div className="container digital-spotlight__inner">
          <div className="digital-spotlight__copy">
            <span className="section-head__label">Dijital Davetiye</span>
            <h2 id="digital-heading" className="digital-spotlight__title">
              Misafirlerinize modern bir deneyim sunun
            </h2>
            <p className="digital-spotlight__desc">
              Zarf animasyonu, müzik ve paylaşılabilir link ile davetiyenizi
              dijital ortamda sunabilirsiniz. Basılı davetiyenize eklediğimiz
              QR kod, misafirlerinizi doğrudan dijital davetiye sayfanıza
              yönlendirir.
            </p>
            <ul className="digital-spotlight__features">
              <li>Animasyonlu zarf açılışı</li>
              <li>Müzik ve fotoğraf galerisi</li>
              <li>Basılı davetiyeye eklenebilir QR kod</li>
              <li>WhatsApp ile kolay paylaşım</li>
            </ul>
            <Link to={`/${featuredDigitalSlug}`} className="btn btn--gold">
              Canlı örneği gör
            </Link>
          </div>
          <DigitalInvitePreview />
        </div>
      </section>

      <section className="showcase" aria-labelledby="invites-heading">
        <div className="container">
          <header className="section-head">
            <span className="section-head__label">Koleksiyon</span>
            <h2 id="invites-heading" className="section-head__title">
              Davetiyeler
            </h2>
            <p className="section-head__desc">
              Düğün, nişan ve sünnet için ekonomik, sade ve lüks davetiye modelleri
            </p>
          </header>
          <div className="showcase__grid showcase__grid--3">
            {davetiyeCategories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/davetiyeler/${cat.slug}`}
                className="showcase-card"
              >
                <div className="showcase-card__media">
                  <OptimizedImage
                    src={getCategoryImage('davetiyeler', cat.slug)}
                    alt=""
                    className="showcase-card__img"
                    loading="lazy"
                  />
                </div>
                <div className="showcase-card__body">
                  <h3 className="showcase-card__title">{cat.shortTitle}</h3>
                  <p className="showcase-card__desc">{cat.description}</p>
                  <span className="showcase-card__link">İncele →</span>
                </div>
              </Link>
            ))}
          </div>
          <div className="showcase__footer">
            <Link to="/davetiyeler" className="btn btn--secondary">
              Tüm davetiyeler
            </Link>
          </div>
        </div>
      </section>

      <section className="showcase showcase--alt" aria-labelledby="candy-heading">
        <div className="container">
          <header className="section-head">
            <span className="section-head__label">Ambalaj</span>
            <h2 id="candy-heading" className="section-head__title">
              Şekerlik
            </h2>
            <p className="section-head__desc">
              Davetiyenizle uyumlu kutu, kese ve etiket setleri
            </p>
          </header>
          <div className="showcase__grid showcase__grid--4">
            {sekerlikCategories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/sekerlik/${cat.slug}`}
                className="showcase-card showcase-card--compact"
              >
                <div className="showcase-card__media">
                  <OptimizedImage
                    src={getCategoryImage('sekerlik', cat.slug)}
                    alt=""
                    className="showcase-card__img"
                    loading="lazy"
                  />
                </div>
                <div className="showcase-card__body">
                  <h3 className="showcase-card__title">{cat.shortTitle}</h3>
                  <span className="showcase-card__link">İncele →</span>
                </div>
              </Link>
            ))}
          </div>
          <div className="showcase__footer">
            <Link to="/sekerlik" className="btn btn--secondary">
              Tüm şekerlikler
            </Link>
          </div>
        </div>
      </section>

      {whatsappHref && (
        <section className="home-cta">
          <div className="container home-cta__inner">
            <h2 className="home-cta__title">Hayalinizdeki davetiyeyi birlikte tasarlayalım</h2>
            <p className="home-cta__desc">
              Renk, kağıt ve baskı seçiminde yanınızdayız. WhatsApp üzerinden
              hızlıca iletişime geçin.
            </p>
            <a
              href={whatsappHref}
              className="btn btn--whatsapp"
              target="_blank"
              rel="noopener noreferrer"
            >
              WhatsApp ile yazın
            </a>
          </div>
        </section>
      )}
    </main>
  );
}
